import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Filters from "./Filters";
import { formatCHF } from "../utils/formatCHF";
import OrderDetailsModal from "../components/Modals/OrderDetailsModal";
import UnpaidOrdersModal from "./UnpaidOrdersModal";

/**
 * ======================================================================
 * dashboardmagasin.jsx
 * ======================================================================
 *
 * DROP-IN COMPLET — TEMPLATE / GUIDE MAINTENEUR
 * ----------------------------------------------------------------------
 * Ce fichier remplace le composant dashboard magasin existant.
 *
 * OBJECTIFS DE CE DROP-IN
 * ----------------------------------------------------------------------
 * 1. Garder l’architecture actuelle
 * 2. Ajouter un filtre par date exacte (calendrier)
 * 3. Afficher les commandes du plus récent au plus ancien
 * 4. Conserver les filtres existants
 * 5. Ne pas casser les modales déjà branchées
 * 6. Ne pas imposer de refactor backend
 *
 * IMPORTANT
 * ----------------------------------------------------------------------
 * - Le filtre calendrier est FRONTEND ONLY
 * - Le filtre rangeDays reste conservé comme garde-fou backend/API
 * - Si une date exacte est choisie, on filtre ensuite côté front
 * - Aucun impact SQL
 * - Aucune migration requise
 *
 * IMPACT
 * ----------------------------------------------------------------------
 * - agit sur l’affichage des commandes
 * - n’impacte pas les données stockées
 * - n’impacte pas les nouvelles/anciennes commandes autrement
 */

/**
 * ======================================================================
 * MAPPING DES FILTRES UI VERS L'API
 * ======================================================================
 *
 * Le dashboard utilise des libellés UI simplifiés.
 * L'API attend des valeurs précises.
 *
 * Exemples :
 * - "pick"      -> "pickpay"
 * - "delivery"  -> "livraison"
 * - "all"       -> "all"
 */
const mapUiTypeToApiType = (uiType) => {
  if (uiType === "pick") return "pickpay";
  if (uiType === "delivery") return "livraison";
  if (uiType === "all") return "all";
  if (uiType === "pickpay") return "pickpay";
  if (uiType === "livraison") return "livraison";
  return "pickpay";
};

/**
 * ======================================================================
 * OUTILS DE SÉCURISATION DES DONNÉES
 * ======================================================================
 *
 * Le backend peut renvoyer `items` :
 * - en tableau
 * - en JSON string
 * - vide / null
 *
 * On normalise toujours vers un tableau exploitable.
 */
const safeParseItems = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * ======================================================================
 * FORMATAGE DE DATE
 * ======================================================================
 *
 * Convertit les dates SQL / ISO vers un affichage fr-CH lisible.
 */
const formatOrderDate = (raw) => {
  if (!raw) return "—";

  const iso =
    typeof raw === "string" && raw.includes(" ") ? raw.replace(" ", "T") : raw;

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(raw);

  return d.toLocaleString("fr-CH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * ======================================================================
 * EXTRACTION SAFE DATE SQL
 * ======================================================================
 *
 * IMPORTANT
 * ----------------------------------------------------------------------
 * Ce helper est utilisé pour comparer une date choisie dans l’input
 * type="date" (format YYYY-MM-DD) avec la date SQL d’une commande.
 *
 * Pourquoi ce choix ?
 * ----------------------------------------------------------------------
 * - évite les bugs timezone JS
 * - évite les décalages de jour
 * - fonctionne très bien avec created_at MySQL
 *
 * Exemple :
 *   "2026-04-10 14:55:00" -> "2026-04-10"
 */
const extractSqlDate = (raw) => {
  if (!raw) return "";
  return String(raw).slice(0, 10);
};

/**
 * ======================================================================
 * LABELS UTILITAIRES
 * ======================================================================
 *
 * statusLabel :
 *   Convertit le statut en affichage upper-case brut.
 *
 * deliveryLabel :
 *   Convertit le mode de livraison en libellé lisible avec icône.
 */
const statusLabel = (s) => String(s || "—").toUpperCase();

const deliveryLabel = (m) =>
  m === "livraison" ? "📦 Livraison" : "🛍️ Pick & Pay";

/**
 * ======================================================================
 * DÉTERMINATION DES FAMILLES DE STATUTS
 * ======================================================================
 *
 * isReady :
 *   indique qu'une commande est prête pour retrait/livraison
 *
 * isCancelled :
 *   commande annulée
 *
 * isDelivered :
 *   commande livrée
 */
const isReady = (s) =>
  ["READY_FOR_PICK", "READY_FOR_DELIVERY"].includes(statusLabel(s));

const isCancelled = (s) => statusLabel(s) === "CANCELLED";

const isDelivered = (s) => statusLabel(s) === "DELIVERED";

/**
 * ======================================================================
 * OUTILS MONÉTAIRES
 * ======================================================================
 *
 * Le dashboard manipule principalement des montants en centimes.
 *
 * toInt :
 *   sécurise une valeur numérique et l'arrondit à un entier.
 *
 * sumItemsCentsRaw :
 *   somme brute des items d'une commande à partir des prix d'items.
 *
 * detectMoneyFactor :
 *   tente de détecter un décalage d'échelle entre total_ttc et items.
 *   Ce mécanisme sert de garde-fou lorsque certaines données historiques
 *   ou hétérogènes ne sont pas parfaitement normalisées.
 *
 * normalizeCents :
 *   applique le facteur détecté pour retrouver un montant exploitable
 *   par formatCHF().
 */
const toInt = (v) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? n : 0;
};

const sumItemsCentsRaw = (items) => {
  if (!Array.isArray(items) || items.length === 0) return 0;

  return items.reduce((acc, it) => {
    const qty = toInt(it?.qty ?? it?.quantite ?? 1) || 1;
    const price = toInt(it?.appliedPrice ?? it?.price ?? 0);
    return acc + price * qty;
  }, 0);
};

const detectMoneyFactor = (order) => {
  const items = order?._items || safeParseItems(order?.items);
  const sumRaw = sumItemsCentsRaw(items);
  const totalRaw = toInt(order?.total_ttc);

  if (totalRaw <= 0) return 1;

  if (sumRaw <= 0) {
    if (totalRaw > 1_000_000) return 100;
    return 1;
  }

  const ratio100 = Math.abs(totalRaw - sumRaw * 100);
  const ratio1 = Math.abs(totalRaw - sumRaw);
  const tolerance = Math.max(5, Math.round(totalRaw * 0.01));

  if (ratio100 <= tolerance && ratio100 < ratio1) return 100;
  return 1;
};

const normalizeCents = (raw, factor) => {
  const n = toInt(raw);
  if (!factor || factor === 1) return n;
  return Math.round(n / factor);
};

/**
 * ======================================================================
 * HELPERS CLIENT
 * ======================================================================
 *
 * Le dashboard doit afficher des informations cohérentes
 * pour deux types de commandes :
 *
 * - client connecté
 * - visiteur (guest)
 *
 * Les helpers suivants unifient :
 * - téléphone
 * - invoice_id
 * - type client
 * - guest label
 * - nom affiché
 * - badge de type client
 */
const getClientPhone = (o) => {
  return (
    o?.guest_phone ||
    o?.client_phone ||
    o?.client_tel ||
    o?.client_telephone ||
    o?.telephone ||
    o?.phone ||
    o?.tel ||
    o?.mobile ||
    ""
  );
};

const getInvoiceIdFromOrder = (o) => {
  const id =
    o?.invoice_id ??
    o?.invoiceId ??
    o?.facture_id ??
    o?.invoice?.id ??
    o?.invoice ??
    null;

  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getCustomerType = (o) => {
  const api = String(o?.customer_type || "").toUpperCase();
  if (api === "GUEST" || api === "USER") return api;

  const uid = Number(o?.user_id ?? 0);
  return uid > 0 ? "USER" : "GUEST";
};

const getGuestLabel = (o) => {
  const api = String(o?.guest_label || "").trim();
  if (api) return api;

  const id = Number(o?.id ?? 0);
  if (!id) return "GUEST";

  return `GUEST-${String(id).padStart(6, "0")}`;
};

const clientDisplayName = (o) => {
  const guestName = String(o?.guest_name || "").trim();
  if (getCustomerType(o) === "GUEST" && guestName) return guestName;

  const prenom = (o?.client_prenom || "").trim();
  const nom = (o?.client_nom || "").trim();
  const full = `${prenom} ${nom}`.trim();

  if (full) return full;

  if (getCustomerType(o) === "GUEST") return getGuestLabel(o);
  return "—";
};

const clientBadge = (o) => {
  const t = getCustomerType(o);

  if (t === "GUEST") {
    return { text: "VISITEUR", title: "Commande visiteur" };
  }

  return { text: "CONNECTÉ", title: "Client connecté" };
};

/**
 * ======================================================================
 * COMPOSANT PRINCIPAL
 * ======================================================================
 */
const DashboardMagasin = () => {
  /**
   * --------------------------------------------------------------------
   * orders
   *   liste brute des commandes renvoyées par l'API magasin
   *
   * initialLoading
   *   chargement initial complet
   *
   * refreshing
   *   rafraîchissement utilisateur / silent refresh
   *
   * errorMsg
   *   message d'erreur visible à l'écran
   *
   * filterType / filterStatus / rangeDays
   *   filtres envoyés à l'API
   *
   * selectedDate
   *   filtre date exact côté frontend
   *
   * viewMode
   *   mode d'affichage local :
   *   - all
   *   - pending
   *   - ready
   *
   * readySort
   *   tri local des commandes prêtes
   *
   * updatingId
   *   id de la commande en cours de passage vers READY
   *
   * selectedOrder
   *   commande actuellement ouverte dans OrderDetailsModal
   *
   * isUnpaidModalOpen
   *   ouverture de la fenêtre dédiée aux commandes non payées
   */
  const [orders, setOrders] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [filterType, setFilterType] = useState("pick");
  const [filterStatus, setFilterStatus] = useState("");
  const [rangeDays, setRangeDays] = useState(7);

  /**
   * ================================================================
   * TEMPLATE — FILTRE DATE EXACTE (CALENDRIER)
   * ================================================================
   *
   * Ce state pilote l’input type="date".
   *
   * Comportement :
   * - vide   => aucun filtre date exact
   * - rempli => on n’affiche que les commandes du jour choisi
   *
   * IMPORTANT :
   * - ne remplace pas les filtres API
   * - s’applique après le fetch
   */
  const [selectedDate, setSelectedDate] = useState("");

  const [viewMode, setViewMode] = useState("all");
  const [readySort, setReadySort] = useState("fifo");

  const [updatingId, setUpdatingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isUnpaidModalOpen, setIsUnpaidModalOpen] = useState(false);

  /**
   * --------------------------------------------------------------------
   * abortRef
   *   annule une requête en cours si on recharge
   *
   * aliveRef
   *   évite les setState après unmount
   *
   * lastFetchAtRef
   *   anti-spam simple sur les requêtes
   */
  const abortRef = useRef(null);
  const aliveRef = useRef(true);
  const lastFetchAtRef = useRef(0);

  /**
   * ====================================================================
   * HELPERS DE PÉRIODE
   * ====================================================================
   */
  const ymd = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  /**
   * ====================================================================
   * URL DE CHARGEMENT DES COMMANDES MAGASIN
   * ====================================================================
   *
   * Cette URL est reconstruite à chaque changement de filtre API.
   *
   * IMPORTANT
   * --------------------------------------------------------------------
   * selectedDate n’est PAS injecté dans l’URL.
   *
   * Pourquoi ?
   * - pour rester drop-in
   * - pour ne pas dépendre d’un endpoint backend modifié
   * - pour garder la compatibilité prod immédiate
   */
  const listUrl = useMemo(() => {
    const params = new URLSearchParams();

    if (filterType) params.set("type", mapUiTypeToApiType(filterType));
    if (filterStatus) params.set("status", filterStatus);

    const now = new Date();
    const fromDate = new Date(now);
    fromDate.setDate(now.getDate() - Number(rangeDays || 7));

    params.set("from", ymd(fromDate));
    params.set("to", ymd(now));
    params.set("limit", "200");

    return `https://marchedeluxe.ch/services/get_orders_magasin.php?${params.toString()}`;
  }, [filterType, filterStatus, rangeDays]);

  /**
   * ====================================================================
   * CHARGEMENT DES COMMANDES
   * ====================================================================
   *
   * fetchOrders charge les commandes du dashboard magasin.
   *
   * Modes :
   * - silent = false : chargement visible
   * - silent = true  : mise à jour sans bruit visuel fort
   *
   * Sécurités :
   * - anti spam léger
   * - abort controller
   * - guard contre component unmounted
   */
  const fetchOrders = useCallback(
    async ({ silent = false } = {}) => {
      const now = Date.now();

      if (now - lastFetchAtRef.current < 350) return;
      lastFetchAtRef.current = now;

      if (!silent) setErrorMsg("");

      if (initialLoading) setInitialLoading(true);
      else if (!silent) setRefreshing(true);

      if (abortRef.current) abortRef.current.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = localStorage.getItem("user_token") || "";

        const res = await fetch(listUrl, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!aliveRef.current) return;

        if (res.status === 401) {
          setErrorMsg("Session expirée. Merci de vous reconnecter.");
          return;
        }

        const data = await res.json().catch(() => ({}));

        if (!data?.success) {
          setErrorMsg(data?.message || "Impossible de charger les commandes.");
          return;
        }

        setOrders(Array.isArray(data.commandes) ? data.commandes : []);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setErrorMsg(err?.message || "Erreur réseau lors du chargement.");
      } finally {
        if (!aliveRef.current) return;
        setInitialLoading(false);
        setRefreshing(false);
      }
    },
    [listUrl, initialLoading]
  );

  /**
   * ====================================================================
   * CYCLE DE VIE
   * ====================================================================
   *
   * 1) recharge dès que listUrl change
   * 2) protège contre les updates après démontage
   */
  useEffect(() => {
    fetchOrders({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUrl]);

  useEffect(() => {
    aliveRef.current = true;

    return () => {
      aliveRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  /**
   * ====================================================================
   * ACTION MAGASIN : MARQUER UNE COMMANDE PRÊTE
   * ====================================================================
   *
   * Cette action appelle :
   *   /services/set_order_status.php
   *
   * avec :
   *   status = READY
   *
   * Le backend reste responsable de décider le statut final exact
   * (ex: READY_FOR_PICK / READY_FOR_DELIVERY) si nécessaire.
   */
  const markReady = useCallback(
    async (orderId) => {
      if (!orderId) return;

      setUpdatingId(orderId);
      setErrorMsg("");

      try {
        const token = localStorage.getItem("user_token") || "";

        const res = await fetch(
          "https://marchedeluxe.ch/services/set_order_status.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ order_id: orderId, status: "READY" }),
          }
        );

        if (res.status === 401) {
          setErrorMsg("Session expirée. Merci de vous reconnecter.");
          return;
        }

        const data = await res.json().catch(() => ({}));

        if (!data?.success) {
          setErrorMsg(
            data?.message || "Impossible de mettre à jour le statut."
          );
          return;
        }

        fetchOrders({ silent: true });
      } catch (err) {
        setErrorMsg(err?.message || "Erreur réseau sur mise à jour statut.");
      } finally {
        setUpdatingId(null);
      }
    },
    [fetchOrders]
  );

  /**
   * ====================================================================
   * ENRICHISSEMENT + FILTRE DATE + DÉCOUPAGE DES COMMANDES
   * ====================================================================
   *
   * Cette étape :
   * - parse les items
   * - détecte le facteur monétaire
   * - calcule un timestamp de tri
   * - applique le filtre date exact si défini
   * - sépare :
   *   - pendingOrders
   *   - readyOrders
   *
   * EXCLUSIONS :
   * - cancelled
   * - delivered
   *
   * TRI :
   * - pending : plus récent -> ancien
   * - ready   : selon readySort
   *
   * NOTE MAINTENEUR
   * --------------------------------------------------------------------
   * Avant ce chantier, le pending utilisait un tri opérationnel orienté :
   *   notes -> livraison -> FIFO
   *
   * Le besoin métier actuel demande un affichage chronologique
   * du plus récent au plus ancien.
   *
   * Donc :
   * - on abandonne volontairement sortOperational
   * - on force le tri date DESC
   */
  const { pendingOrders, readyOrders } = useMemo(() => {
    const enriched = (Array.isArray(orders) ? orders : []).map((o) => {
      const items = safeParseItems(o?.items);
      const factor = detectMoneyFactor({ ...o, _items: items });
      const ts =
        new Date(String(o?.created_at || "").replace(" ", "T")).getTime() || 0;

      return { ...o, _items: items, _moneyFactor: factor, _ts: ts };
    });

    /**
     * ================================================================
     * TEMPLATE — FILTRE DATE EXACTE
     * ================================================================
     *
     * Si selectedDate existe :
     * - on ne conserve que les commandes de ce jour
     * - comparaison stable via extractSqlDate()
     */
    let filtered = enriched;

    if (selectedDate) {
      filtered = enriched.filter((o) => {
        return extractSqlDate(o?.created_at) === selectedDate;
      });
    }

    const ready = [];
    const pending = [];

    for (const o of filtered) {
      if (isCancelled(o.status) || isDelivered(o.status)) continue;

      if (isReady(o.status)) ready.push(o);
      else pending.push(o);
    }

    /**
     * ================================================================
     * TEMPLATE — TRI CHRONO GLOBAL
     * ================================================================
     *
     * Pending :
     * - toujours du plus récent au plus ancien
     *
     * Ready :
     * - garde la possibilité FIFO/LIFO
     * - mais basée sur _ts cohérent
     */
    pending.sort((a, b) => (b._ts || 0) - (a._ts || 0));

    ready.sort((a, b) =>
      readySort === "lifo"
        ? (b._ts || 0) - (a._ts || 0)
        : (a._ts || 0) - (b._ts || 0)
    );

    return { pendingOrders: pending, readyOrders: ready };
  }, [orders, readySort, selectedDate]);

  /**
   * ====================================================================
   * OUVERTURE / FERMETURE DES MODALES
   * ====================================================================
   */
  const openOrderModal = useCallback((order) => setSelectedOrder(order), []);
  const closeOrderModal = useCallback(() => setSelectedOrder(null), []);

  const modalHelpers = useMemo(
    () => ({
      getClientPhone,
      getInvoiceIdFromOrder,
      deliveryLabel,
      statusLabel,
      formatOrderDate,
      normalizeCents,
    }),
    []
  );

  /**
   * ====================================================================
   * SOUS-COMPOSANT : OrderCell
   * ====================================================================
   *
   * Rendu compact d'une commande dans le tableau principal.
   *
   * Contient :
   * - identification commande
   * - type livraison
   * - type client
   * - date
   * - client
   * - créneau
   * - total
   * - statut
   * - aperçu des items
   * - note éventuelle
   */
  const OrderCell = ({ o }) => {
    const badge = clientBadge(o);
    const who = clientDisplayName(o);
    const isGuest = getCustomerType(o) === "GUEST";

    return (
      <>
        <div className="mdx-cardline">
          <div>
            <div className="mdx-strong">
              #{o.id} • {deliveryLabel(o.delivery_mode)}
              {o?.notes?.trim() ? (
                <span
                  className="mdx-pill mdx-pill-note"
                  title="Contient une note"
                >
                  📝 Note
                </span>
              ) : null}
              <span
                className={`mdx-pill ${
                  isGuest ? "mdx-pill-guest" : "mdx-pill-user"
                }`}
                title={badge.title}
                style={{ marginLeft: 10 }}
              >
                {isGuest ? "👤" : "✅"} {badge.text}
              </span>
            </div>

            <div className="mdx-meta">{formatOrderDate(o.created_at)}</div>

            <div className="mdx-meta" style={{ marginTop: 6 }}>
              Client : <strong>{who}</strong>
              {isGuest ? (
                <span style={{ opacity: 0.75 }}> • {getGuestLabel(o)}</span>
              ) : null}
            </div>

            {o.horaire_pickpay ? (
              <div className="mdx-meta">
                Créneau : <strong>{o.horaire_pickpay}</strong>
              </div>
            ) : null}
          </div>

          <div className="mdx-rightcol">
            <div className="mdx-strong">
              {formatCHF(normalizeCents(o.total_ttc, o._moneyFactor))}
            </div>
            <div className="mdx-status">{statusLabel(o.status)}</div>
          </div>
        </div>

        {o._items?.length ? (
          <div className="mdx-items">
            {o._items.map((it, idx) => {
              const qty = Number(it?.qty ?? it?.quantite ?? 1) || 1;
              const name =
                it?.product_name ||
                it?.name ||
                it?.nom ||
                (it?.id ? `Produit #${it.id}` : "Produit");

              const p = it?.appliedPrice ?? it?.price ?? null;

              return (
                <div key={`${o.id}-${idx}`} className="mdx-item">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div>
                      <strong>{qty}×</strong> {name}
                    </div>

                    {/* 🔥 DETAILS PLATEAU */}
                    {it?.details_label && (
                      <div style={{ fontSize: "12px", opacity: 0.75 }}>
                        {it.details_label}
                      </div>
                    )}
                  </div>

                  <div className="mdx-nowrap">
                    {p == null
                      ? "—"
                      : formatCHF(normalizeCents(p, o._moneyFactor))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mdx-meta" style={{ marginTop: 10 }}>
            Articles : <strong>indisponibles</strong>
          </div>
        )}

        {o.notes?.trim() ? (
          <div className="mdx-note">Note : {o.notes}</div>
        ) : null}
      </>
    );
  };

  /**
   * ====================================================================
   * VISIBILITÉ DES SECTIONS
   * ====================================================================
   */
  const showPending = viewMode === "all" || viewMode === "pending";
  const showReady = viewMode === "all" || viewMode === "ready";

  /**
   * ====================================================================
   * LIBELLÉ D’AIDE DU FILTRE DATE
   * ====================================================================
   *
   * Petit helper UI pour rendre le filtre plus explicite.
   */
  const selectedDateLabel = selectedDate
    ? `Date filtrée : ${selectedDate}`
    : "Aucune date précise sélectionnée";

  /**
   * ====================================================================
   * RENDU
   * ====================================================================
   */
  return (
    <div className="dashboard-magasin-container p-6">
      <style>{`
        .mdx-wrap {
          max-width: 1100px;
          margin: 0 auto;
          width: 100%;
        }

        .mdx-header {
          display:flex;
          justify-content:space-between;
          align-items:flex-start;
          gap:12px;
          margin-bottom:14px;
        }

        .mdx-title {
          margin:0;
          line-height:1.15;
        }

        .mdx-sub {
          opacity:.75;
          font-size:13px;
          margin-top:4px;
        }

        .mdx-split {
          display:grid;
          grid-template-columns: 1fr;
          gap:14px;
          margin-top:12px;
        }

        .mdx-section {
          border:1px solid rgba(0,0,0,.08);
          border-radius:14px;
          background:rgba(255,255,255,.75);
          box-shadow:0 10px 28px rgba(0,0,0,.06);
          overflow:hidden;
        }

        .mdx-section-h {
          padding:14px 16px;
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap: 10px;
          border-bottom:1px solid rgba(0,0,0,.06);
          background:rgba(0,0,0,.02);
        }

        .mdx-section-h h2 {
          margin:0;
          font-size:14px;
          letter-spacing:.4px;
          text-transform:uppercase;
        }

        .mdx-count {
          opacity:.7;
          font-size:12px;
        }

        .mdx-table {
          width:100%;
          border-collapse:separate;
          border-spacing:0;
        }

        .mdx-th {
          text-align:left;
          font-size:12px;
          letter-spacing:.3px;
          text-transform:uppercase;
          opacity:.75;
          padding:12px 16px;
          border-bottom:1px solid rgba(0,0,0,.06);
          background:rgba(255,255,255,.6);
        }

        .mdx-td {
          vertical-align:top;
          padding:14px 16px;
          border-bottom:1px solid rgba(0,0,0,.06);
        }

        .mdx-cardline {
          display:flex;
          justify-content:space-between;
          gap:12px;
        }

        .mdx-rightcol {
          text-align:right;
        }

        .mdx-strong {
          font-weight:700;
        }

        .mdx-meta {
          opacity:.75;
          font-size:12px;
          margin-top:4px;
          word-break: break-word;
        }

        .mdx-status {
          display:inline-flex;
          align-items:center;
          padding:6px 10px;
          border-radius:999px;
          font-size:12px;
          border:1px solid rgba(0,0,0,.12);
          background:rgba(0,0,0,.03);
          margin-top: 8px;
        }

        .mdx-items {
          margin-top:10px;
          display:grid;
          gap:6px;
        }

        .mdx-item {
          display:flex;
          justify-content:space-between;
          gap:10px;
          padding:8px 10px;
          border-radius:10px;
          background:rgba(0,0,0,.03);
        }

        .mdx-note {
          margin-top:10px;
          padding:10px 12px;
          border-radius:10px;
          background:rgba(0,0,0,.03);
          font-style:italic;
          opacity:.85;
          word-break: break-word;
        }

        .mdx-empty {
          padding:16px;
          opacity:.75;
        }

        .mdx-error {
          margin: 10px 0 0;
          padding:12px;
          border-radius:12px;
          border:1px solid rgba(255,0,0,.25);
          background:rgba(255,0,0,.06);
        }

        .mdx-refresh {
          opacity:.7;
          font-size:13px;
          margin-top:8px;
        }

        .mdx-range-select {
          height: 38px;
          border: 1px solid rgba(0,0,0,.12);
          border-radius: 8px;
          padding: 0 10px;
          background: white;
          font-size: 13px;
          max-width: 100%;
        }

        .mdx-actions {
          display:flex;
          gap:10px;
          align-items:center;
          justify-content:flex-end;
          flex-wrap: wrap;
        }

        .mdx-btn {
          border:1px solid rgba(0,0,0,.12);
          background:rgba(255,255,255,.8);
          padding:8px 10px;
          border-radius:10px;
          font-size:12px;
          cursor:pointer;
        }

        .mdx-btn:disabled {
          opacity:.5;
          cursor:not-allowed;
        }

        .mdx-pill {
          display:inline-flex;
          align-items:center;
          gap:6px;
          margin-left:10px;
          padding:4px 8px;
          border-radius:999px;
          font-size:12px;
          border:1px solid rgba(0,0,0,.12);
          background:rgba(0,0,0,.03);
          vertical-align: middle;
        }

        .mdx-pill-note {
          border-color: rgba(255,193,7,.35);
          background: rgba(255,193,7,.10);
        }

        .mdx-pill-guest {
          border-color: rgba(0,0,0,.16);
          background: rgba(0,0,0,.03);
        }

        .mdx-pill-user {
          border-color: rgba(39,174,96,.25);
          background: rgba(39,174,96,.08);
        }

        .mdx-rowclick {
          cursor:pointer;
        }

        .mdx-rowclick:hover td {
          background: rgba(0,0,0,.02);
        }

        .mdx-topfilters {
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          align-items:center;
          justify-content:space-between;
          margin: 8px 0 12px;
        }

        .mdx-chip {
          border:1px solid rgba(0,0,0,.12);
          background:rgba(255,255,255,.85);
          padding:8px 10px;
          border-radius:10px;
          font-size:12px;
          cursor:pointer;
        }

        .mdx-chip.active {
          border-color: rgba(39,174,96,.35);
          background: rgba(39,174,96,.08);
          font-weight:700;
        }

        .mdx-nowrap {
          white-space: nowrap;
        }

        .mdx-unpaid-launch {
          border: 1px solid rgba(212,175,55,.35);
          background: rgba(255,248,225,.88);
          color: #8b4513;
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 13px;
          cursor: pointer;
          white-space: nowrap;
        }

        .mdx-unpaid-launch:hover {
          background: rgba(255,248,225,.96);
        }

        .mdx-date-filter-wrap {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
          margin-top: 10px;
        }

        .mdx-date-filter-label {
          font-size: 12px;
          opacity: .7;
        }

        @media (max-width: 1024px) {
          .dashboard-magasin-container {
            padding: 18px 14px !important;
          }

          .mdx-wrap {
            max-width: 100%;
          }

          .mdx-header {
            flex-direction: column;
            align-items: stretch;
          }

          .mdx-header > div:last-child {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            width: 100%;
          }

          .mdx-header .btn-luxury-outline,
          .mdx-header .mdx-range-select,
          .mdx-header .mdx-unpaid-launch {
            flex: 1 1 220px;
          }

          .mdx-topfilters {
            flex-direction: column;
            align-items: stretch;
          }

          .mdx-topfilters > div {
            width: 100%;
            justify-content: flex-start;
          }

          .mdx-table {
            table-layout: fixed;
          }

          .mdx-th,
          .mdx-td {
            padding: 12px;
          }

          .mdx-cardline {
            flex-direction: column;
          }

          .mdx-rightcol {
            text-align: left;
          }

          .mdx-actions {
            justify-content: flex-start;
          }
        }

        @media (max-width: 780px) {
          .dashboard-magasin-container {
            padding: 14px 10px 100px !important;
          }

          .mdx-title {
            font-size: 1.7rem;
          }

          .mdx-sub {
            font-size: 12px;
            line-height: 1.45;
          }

          .mdx-header {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .mdx-header > div:last-child {
            display: grid;
            grid-template-columns: 1fr;
            gap: 10px;
            width: 100%;
          }

          .mdx-header .btn-luxury-outline,
          .mdx-header .mdx-range-select,
          .mdx-header .mdx-unpaid-launch {
            width: 100%;
            min-width: 0;
          }

          .mdx-topfilters {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }

          .mdx-topfilters > div {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .mdx-chip,
          .mdx-range-select {
            width: 100%;
            box-sizing: border-box;
          }

          .mdx-section-h {
            flex-direction: column;
            align-items: flex-start;
          }

          .mdx-table,
          .mdx-table thead,
          .mdx-table tbody,
          .mdx-table tr,
          .mdx-table th,
          .mdx-table td {
            display: block;
            width: 100%;
          }

          .mdx-table thead {
            display: none;
          }

          .mdx-table tbody {
            display: grid;
            gap: 12px;
            padding: 12px;
          }

          .mdx-table tr {
            border: 1px solid rgba(0,0,0,.08);
            border-radius: 14px;
            background: rgba(255,255,255,.9);
            overflow: hidden;
          }

          .mdx-rowclick:hover td {
            background: transparent;
          }

          .mdx-td {
            border-bottom: 1px solid rgba(0,0,0,.06);
            padding: 12px;
          }

          .mdx-td:last-child {
            border-bottom: none;
          }

          .mdx-td::before {
            content: attr(data-label);
            display: block;
            margin-bottom: 8px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: .08em;
            text-transform: uppercase;
            opacity: .65;
          }

          .mdx-cardline {
            flex-direction: column;
            gap: 10px;
          }

          .mdx-rightcol {
            text-align: left;
          }

          .mdx-pill {
            margin-left: 0;
            margin-top: 8px;
            white-space: normal;
          }

          .mdx-item {
            flex-direction: column;
            align-items: flex-start;
          }

          .mdx-actions {
            flex-direction: column;
            align-items: stretch;
            justify-content: stretch;
          }

          .mdx-btn {
            width: 100%;
            min-height: 42px;
          }

          .mdx-nowrap {
            white-space: normal;
          }

          .mdx-date-filter-wrap {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>

      <div className="mdx-wrap">
        <header className="mdx-header">
          <div>
            <h1 className="text-3xl font-serif text-gold mdx-title">
              Gestion des Commandes
            </h1>

            <div className="mdx-sub">
              Affichage chronologique : plus récent → plus ancien • Filtrage par
              date exacte disponible
            </div>

            {refreshing ? (
              <div className="mdx-refresh">Mise à jour…</div>
            ) : null}
          </div>

          <div className="flex gap-3">
            <select
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
              className="mdx-range-select"
              aria-label="Période d’affichage"
            >
              <option value={1}>24h</option>
              <option value={3}>3 jours</option>
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={30}>30 jours</option>
            </select>

            <button
              type="button"
              onClick={() => setIsUnpaidModalOpen(true)}
              className="mdx-unpaid-launch"
              aria-label="Voir les commandes non payées"
              title="Ouvrir la fenêtre des commandes non payées"
            >
              Voir commandes non payées
            </button>

            <button
              type="button"
              onClick={() => fetchOrders({ silent: false })}
              className="btn-luxury-outline text-sm"
              disabled={refreshing || initialLoading}
              aria-label="Actualiser la liste des commandes"
            >
              {refreshing ? "Actualisation..." : "Actualiser"}
            </button>
          </div>
        </header>

        <div style={{ marginBottom: 10 }}>
          <Filters
            filterType={filterType}
            setFilterType={setFilterType}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
          />

          {/* ============================================================
             TEMPLATE — FILTRE DATE UI
             ============================================================
             Ce bloc ajoute le calendrier.
             Il n’impacte pas le backend.
          */}
          <div className="mdx-date-filter-wrap">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="mdx-range-select"
              style={{ height: 38 }}
              aria-label="Filtrer les commandes par date exacte"
              title="Afficher uniquement les commandes de cette date"
            />

            {selectedDate && (
              <button
                type="button"
                onClick={() => setSelectedDate("")}
                className="mdx-btn"
              >
                Réinitialiser date
              </button>
            )}

            <span className="mdx-date-filter-label">{selectedDateLabel}</span>
          </div>
        </div>

        <div className="mdx-topfilters">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className={`mdx-chip ${viewMode === "all" ? "active" : ""}`}
              onClick={() => setViewMode("all")}
            >
              Tout
            </button>

            <button
              type="button"
              className={`mdx-chip ${viewMode === "pending" ? "active" : ""}`}
              onClick={() => setViewMode("pending")}
            >
              À préparer
            </button>

            <button
              type="button"
              className={`mdx-chip ${viewMode === "ready" ? "active" : ""}`}
              onClick={() => setViewMode("ready")}
            >
              Prêtes
            </button>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 12, opacity: 0.7 }}>Tri des prêtes :</span>

            <select
              value={readySort}
              onChange={(e) => setReadySort(e.target.value)}
              className="mdx-range-select"
              style={{ height: 34 }}
              aria-label="Tri des commandes prêtes"
            >
              <option value="fifo">Ancien → Récent</option>
              <option value="lifo">Récent → Ancien</option>
            </select>
          </div>
        </div>

        {errorMsg ? (
          <div className="mdx-error" role="alert" aria-live="polite">
            <strong>Erreur :</strong> {errorMsg}
          </div>
        ) : null}

        {initialLoading ? (
          <div className="flex justify-center p-10" style={{ padding: 24 }}>
            <div className="loader-luxury"></div>
          </div>
        ) : (
          <div className="mdx-split">
            {showPending && (
              <section
                className="mdx-section"
                aria-label="Commandes à préparer"
              >
                <div className="mdx-section-h">
                  <h2>À préparer</h2>
                  <div className="mdx-count">
                    {pendingOrders.length} commande(s)
                  </div>
                </div>

                {pendingOrders.length === 0 ? (
                  <div className="mdx-empty">
                    Rien à préparer pour le moment.
                  </div>
                ) : (
                  <table className="mdx-table">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="mdx-th"
                          style={{ width: "58%" }}
                        >
                          Commande
                        </th>

                        <th
                          scope="col"
                          className="mdx-th"
                          style={{ width: "22%" }}
                        >
                          Client
                        </th>

                        <th
                          scope="col"
                          className="mdx-th"
                          style={{ width: "20%" }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {pendingOrders.map((o) => {
                        const phone = getClientPhone(o);
                        const isGuest = getCustomerType(o) === "GUEST";

                        return (
                          <tr
                            key={o.id}
                            className="mdx-rowclick"
                            onClick={() => openOrderModal(o)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openOrderModal(o);
                              }
                            }}
                            aria-label={`Ouvrir la commande ${o.id}`}
                          >
                            <td className="mdx-td" data-label="Commande">
                              <OrderCell o={o} />
                            </td>

                            <td className="mdx-td" data-label="Client">
                              <div className="mdx-strong">
                                {clientDisplayName(o) || "—"}
                              </div>

                              <div className="mdx-meta">
                                Type :{" "}
                                <strong>
                                  {isGuest ? "Visiteur" : "Connecté"}
                                </strong>
                                {isGuest ? ` • ${getGuestLabel(o)}` : ""}
                              </div>

                              <div className="mdx-meta">
                                Email : <strong>{o.client_email || "—"}</strong>
                              </div>

                              <div className="mdx-meta">
                                Tél : <strong>{phone || "—"}</strong>
                              </div>
                            </td>

                            <td
                              className="mdx-td"
                              data-label="Actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="mdx-actions">
                                <button
                                  type="button"
                                  className="mdx-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    markReady(o.id);
                                  }}
                                  disabled={updatingId === o.id}
                                  aria-label={`Marquer la commande ${o.id} comme prête`}
                                  title="Marquer la commande comme prête"
                                >
                                  {updatingId === o.id
                                    ? "..."
                                    : "✅ Marquer prête"}
                                </button>

                                <button
                                  type="button"
                                  className="mdx-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openOrderModal(o);
                                  }}
                                  aria-label={`Voir détails et facture de la commande ${o.id}`}
                                  title="Ouvrir détails + facture"
                                >
                                  👁️ Voir
                                </button>
                              </div>

                              <div
                                className="mdx-meta"
                                style={{ marginTop: 10 }}
                              >
                                Statut :{" "}
                                <strong>{statusLabel(o.status)}</strong>
                              </div>

                              <div className="mdx-meta">
                                Facture :{" "}
                                <strong>
                                  {getInvoiceIdFromOrder(o) ? "✅ OK" : "—"}
                                </strong>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            )}

            {showReady && (
              <section className="mdx-section" aria-label="Commandes prêtes">
                <div className="mdx-section-h">
                  <h2>Prêtes</h2>
                  <div className="mdx-count">
                    {readyOrders.length} commande(s)
                  </div>
                </div>

                {readyOrders.length === 0 ? (
                  <div className="mdx-empty">Aucune commande prête.</div>
                ) : (
                  <table className="mdx-table">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="mdx-th"
                          style={{ width: "58%" }}
                        >
                          Commande
                        </th>

                        <th
                          scope="col"
                          className="mdx-th"
                          style={{ width: "22%" }}
                        >
                          Client
                        </th>

                        <th
                          scope="col"
                          className="mdx-th"
                          style={{ width: "20%" }}
                        >
                          Infos
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {readyOrders.map((o) => {
                        const phone = getClientPhone(o);
                        const isGuest = getCustomerType(o) === "GUEST";

                        return (
                          <tr
                            key={o.id}
                            className="mdx-rowclick"
                            onClick={() => openOrderModal(o)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openOrderModal(o);
                              }
                            }}
                            aria-label={`Ouvrir la commande ${o.id}`}
                          >
                            <td className="mdx-td" data-label="Commande">
                              <OrderCell o={o} />
                            </td>

                            <td className="mdx-td" data-label="Client">
                              <div className="mdx-strong">
                                {clientDisplayName(o) || "—"}
                              </div>

                              <div className="mdx-meta">
                                Type :{" "}
                                <strong>
                                  {isGuest ? "Visiteur" : "Connecté"}
                                </strong>
                                {isGuest ? ` • ${getGuestLabel(o)}` : ""}
                              </div>

                              <div className="mdx-meta">
                                Email : <strong>{o.client_email || "—"}</strong>
                              </div>

                              <div className="mdx-meta">
                                Tél : <strong>{phone || "—"}</strong>
                              </div>
                            </td>

                            <td className="mdx-td" data-label="Infos">
                              <div className="mdx-meta">
                                Articles :{" "}
                                <strong>{o._items?.length ?? 0}</strong>
                              </div>

                              <div className="mdx-meta">
                                Créneau :{" "}
                                <strong>
                                  {o.horaire_pickpay?.trim()
                                    ? o.horaire_pickpay
                                    : "—"}
                                </strong>
                              </div>

                              <div className="mdx-meta">
                                Note :{" "}
                                <strong>{o.notes?.trim() ? "Oui" : "—"}</strong>
                              </div>

                              <div
                                className="mdx-meta"
                                style={{ marginTop: 10 }}
                              >
                                Statut :{" "}
                                <strong>{statusLabel(o.status)}</strong>
                              </div>

                              <div
                                className="mdx-meta"
                                style={{ marginTop: 10 }}
                              >
                                <span style={{ opacity: 0.7 }}>Détails :</span>{" "}
                                <button
                                  type="button"
                                  className="mdx-btn"
                                  style={{ padding: "6px 10px", marginLeft: 8 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openOrderModal(o);
                                  }}
                                >
                                  👁️ Voir
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            )}
          </div>
        )}
      </div>

      <OrderDetailsModal
        isOpen={!!selectedOrder}
        order={selectedOrder}
        onClose={closeOrderModal}
        helpers={modalHelpers}
      />

      <UnpaidOrdersModal
        isOpen={isUnpaidModalOpen}
        onClose={() => setIsUnpaidModalOpen(false)}
      />
    </div>
  );
};

export default DashboardMagasin;
